import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, X, Wrench, Package, AlertTriangle } from "lucide-react";
import { api, errMsg, uploadPhoto, getProfile, photoUrl } from "./api";
import { Panel, BigBtn, GhostBtn, Field, inputCls, Spinner } from "./ui";

export const STATUS_UI = {
  operational: { label: "Operational", cls: "text-emerald-400 border-emerald-500/60", bg: "bg-emerald-500/10" },
  faulty: { label: "Faulty / Out of service", cls: "text-[#ff2e4d] border-[#ff2e4d]", bg: "bg-[#ff2e4d]/10" },
  awaiting_parts: { label: "Awaiting parts", cls: "text-amber-400 border-amber-400/70", bg: "bg-amber-400/10" },
};
const EV_CLS = {
  red: "border-[#ff2e4d] text-[#ff2e4d]", amber: "border-amber-400 text-amber-400",
  blue: "border-sky-400 text-sky-400", purple: "border-purple-400 text-purple-400",
  green: "border-emerald-400 text-emerald-400", neutral: "border-white/25 text-white/50",
};
const EV_LABEL = {
  fault: "Fault reported", update: "Update", parts_ordered: "Parts ordered",
  parts_received: "Parts received", parts_installed: "Parts installed", parts_cancelled: "Order cancelled",
  part_replaced: "Part replaced", repair: "Repair", service: "Service completed",
  resolved: "Fault resolved", returned_to_service: "Returned to service",
  void: "Record voided", reopen: "Fault reopened", assessment: "Assessment",
};
const fmtDT = (s) => (s ? new Date(s).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-AU", { dateStyle: "medium" }) : "—");

function PhotoBtn({ photoId, setPhotoId, refName }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-white/60 min-h-[44px] cursor-pointer">
      <Camera size={18} /> <span>{photoId ? "Photo attached ✓" : "Add photo (optional)"}</span>
      <input type="file" accept="image/*" className="hidden" data-testid="bike-photo-input"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          try { setPhotoId(await uploadPhoto(f, refName)); toast.success("Photo uploaded"); }
          catch (ex) { toast.error(errMsg(ex)); }
        }} />
    </label>
  );
}

function ReportFault({ num, openIncidents, onDone, onCancel }) {
  const [mode, setMode] = useState(openIncidents.length ? "choose" : "new");
  const [target, setTarget] = useState(openIncidents[0]?.incident_id || "");
  const [desc, setDesc] = useState("");
  const [earlier, setEarlier] = useState(false);
  const [occurredAt, setOccurredAt] = useState("");
  const [photoId, setPhotoId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clientRef] = useState(() => crypto.randomUUID());

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "update") {
        await api.post(`/bikes/${num}/incidents/${target}/update`, { note: desc, photos: photoId ? [photoId] : [] });
        toast.success("Update added to the existing fault — fault count unchanged");
      } else {
        const { data } = await api.post(`/bikes/${num}/incidents`, {
          description: desc, photos: photoId ? [photoId] : [],
          occurred_at: earlier && occurredAt ? occurredAt : null, client_ref: clientRef,
        });
        toast.success(`Fault ${data.incident.incident_id} recorded — team emailed`);
        if (data.notice) toast.info(data.notice, { duration: 8000 });
      }
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (mode === "choose") return (
    <div className="space-y-3 pt-3 border-t border-white/10 mt-3" data-testid="fault-choose">
      <p className="text-sm text-amber-400 font-semibold flex items-center gap-2"><AlertTriangle size={16} /> This bike already has an open fault</p>
      {openIncidents.map((i) => (
        <div key={i.incident_id} className="rounded-xl border border-white/15 p-3 text-sm">
          <p className="text-white/80">{i.incident_id} — {i.description}</p>
        </div>
      ))}
      <p className="text-xs text-white/50">If it's the same unresolved issue, add an update instead of a new report — this keeps the fault count accurate.</p>
      <div className="grid grid-cols-1 gap-2">
        <BigBtn data-testid="fault-update-existing" onClick={() => setMode("update")}>Update an existing fault</BigBtn>
        <GhostBtn data-testid="fault-report-new" onClick={() => setMode("new")}>It's a different problem — report a new fault</GhostBtn>
        <button onClick={onCancel} className="text-xs text-white/40 min-h-[44px]">Cancel</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-3 pt-3 border-t border-white/10 mt-3" data-testid="fault-form">
      {mode === "update" && openIncidents.length > 1 && (
        <Field label="Which fault?">
          <select data-testid="fault-target" value={target} onChange={(e) => setTarget(e.target.value)} className={inputCls}>
            {openIncidents.map((i) => <option key={i.incident_id} value={i.incident_id}>{i.incident_id} — {i.description.slice(0, 50)}</option>)}
          </select>
        </Field>
      )}
      <Field label={mode === "update" ? "Update note" : "Fault description (required)"}>
        <textarea data-testid="fault-description" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2}
          className={inputCls + " py-3 min-h-[56px]"} placeholder={mode === "update" ? "What's changed / extra detail…" : "Short fault description…"} />
      </Field>
      {mode === "new" && (
        <>
          <label className="flex items-center gap-3 min-h-[44px] cursor-pointer text-sm text-white/60">
            <input data-testid="fault-earlier-toggle" type="checkbox" checked={earlier} onChange={(e) => setEarlier(e.target.checked)} className="h-5 w-5 accent-[#ff2e4d]" />
            The fault actually happened earlier
          </label>
          {earlier && <input data-testid="fault-occurred-at" type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} className={inputCls} />}
        </>
      )}
      <PhotoBtn photoId={photoId} setPhotoId={setPhotoId} refName={`bike:${num}`} />
      <BigBtn data-testid="fault-save" onClick={submit} disabled={busy || !desc.trim()}>
        {busy ? "Saving…" : mode === "update" ? "Save update (no new fault count)" : "Save fault report"}
      </BigBtn>
      <button onClick={onCancel} className="text-xs text-white/40 min-h-[44px] w-full">Cancel</button>
    </div>
  );
}

function FixBike({ num, openIncidents, onDone, onCancel }) {
  const [type, setType] = useState(null);
  const [desc, setDesc] = useState("");
  const [part, setPart] = useState("");
  const [qty, setQty] = useState("");
  const [photoId, setPhotoId] = useState(null);
  const [linked, setLinked] = useState(openIncidents.map((i) => i.incident_id));
  const [earlier, setEarlier] = useState(false);
  const [performedAt, setPerformedAt] = useState("");
  const [stage, setStage] = useState("form");
  const [busy, setBusy] = useState(false);
  const [clientRef] = useState(() => crypto.randomUUID());

  const save = async (outcome) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bikes/${num}/work`, {
        work_type: type, description: desc, part: part || null, quantity: qty || null,
        photos: photoId ? [photoId] : [], incident_ids: outcome === "routine" ? [] : linked,
        performed_at: earlier && performedAt ? performedAt : null, outcome, client_ref: clientRef,
      });
      if (outcome === "resolved" && data.bike?.available) toast.success("Repair complete — bike returned to service ✓");
      else if (outcome === "resolved") toast.success("Fault resolved — but another fault still keeps this bike out of service");
      else if (outcome === "further") toast.info("Work recorded — bike kept out of service");
      else toast.success("Routine work recorded");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const TYPES = [["replace_part", "Replace part"], ["service", "Service bike"], ["other", "Other repair"]];
  const valid = desc.trim() && (type !== "replace_part" || part.trim());

  if (!type) return (
    <div className="space-y-2 pt-3 border-t border-white/10 mt-3" data-testid="fix-choose">
      <p className="text-xs uppercase tracking-widest text-white/40">Fix bike — what was done?</p>
      {TYPES.map(([k, l]) => <BigBtn key={k} data-testid={`fix-type-${k}`} className="!bg-white/10 !text-white" onClick={() => setType(k)}>{l}</BigBtn>)}
      <button onClick={onCancel} className="text-xs text-white/40 min-h-[44px] w-full">Cancel</button>
    </div>
  );

  if (stage === "outcome") return (
    <div className="space-y-3 pt-3 border-t border-white/10 mt-3" data-testid="fix-outcome">
      <p className="text-sm font-semibold text-white/90">Has this work resolved the fault, and has the bike been checked and confirmed ready for use?</p>
      <BigBtn data-testid="outcome-resolved" disabled={busy || linked.length === 0} onClick={() => save("resolved")}
        className="!bg-emerald-500 !text-black">Repair complete — return bike to service</BigBtn>
      <BigBtn data-testid="outcome-further" disabled={busy} onClick={() => save("further")}
        className="!bg-white/10 !text-white">Further work required — keep bike out of service</BigBtn>
      {linked.length === 0 && <p className="text-xs text-amber-400">No fault linked — go back and tick the fault this work fixes, or save as routine work.</p>}
      <GhostBtn data-testid="outcome-routine" disabled={busy} onClick={() => save("routine")} className="w-full">Routine / preventative — no fault involved</GhostBtn>
      <button onClick={() => setStage("form")} className="text-xs text-white/40 min-h-[44px] w-full">Back</button>
    </div>
  );

  return (
    <div className="space-y-3 pt-3 border-t border-white/10 mt-3" data-testid="fix-form">
      <p className="text-xs uppercase tracking-widest text-white/40">{TYPES.find(([k]) => k === type)[1]} — Bike {String(num).padStart(2, "0")}</p>
      {type === "replace_part" && (
        <Field label="Part replaced (required)">
          <input data-testid="fix-part" value={part} onChange={(e) => setPart(e.target.value)} className={inputCls} placeholder="e.g. Left pedal" />
        </Field>
      )}
      <Field label={type === "service" ? "Service details / work completed (required)" : "Work performed (required)"}>
        <textarea data-testid="fix-description" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} className={inputCls + " py-3 min-h-[56px]"} />
      </Field>
      {type !== "replace_part" && (
        <Field label={type === "service" ? "Parts replaced during the service (optional)" : "Parts used (optional)"}>
          <input data-testid="fix-parts-optional" value={part} onChange={(e) => setPart(e.target.value)} className={inputCls} />
        </Field>
      )}
      {type === "replace_part" && (
        <Field label="Quantity (optional)">
          <input data-testid="fix-qty" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} inputMode="numeric" />
        </Field>
      )}
      {openIncidents.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-widest text-white/50">This work addresses:</p>
          {openIncidents.map((i) => (
            <label key={i.incident_id} className="flex items-center gap-3 min-h-[44px] cursor-pointer text-sm">
              <input type="checkbox" data-testid={`fix-link-${i.incident_id}`} className="h-5 w-5 accent-[#ff2e4d]"
                checked={linked.includes(i.incident_id)}
                onChange={(e) => setLinked(e.target.checked ? [...linked, i.incident_id] : linked.filter((x) => x !== i.incident_id))} />
              <span className="text-white/80">{i.incident_id} — {i.description.slice(0, 60)}</span>
            </label>
          ))}
        </div>
      )}
      <label className="flex items-center gap-3 min-h-[44px] cursor-pointer text-sm text-white/60">
        <input type="checkbox" checked={earlier} onChange={(e) => setEarlier(e.target.checked)} className="h-5 w-5 accent-[#ff2e4d]" />
        Work was completed earlier
      </label>
      {earlier && <input data-testid="fix-performed-at" type="datetime-local" value={performedAt} onChange={(e) => setPerformedAt(e.target.value)} className={inputCls} />}
      <PhotoBtn photoId={photoId} setPhotoId={setPhotoId} refName={`bike:${num}:work`} />
      <BigBtn data-testid="fix-continue" onClick={() => setStage("outcome")} disabled={!valid}>Continue</BigBtn>
      <button onClick={() => setType(null)} className="text-xs text-white/40 min-h-[44px] w-full">Back</button>
    </div>
  );
}

function OrderParts({ num, openIncidents, onDone, onCancel }) {
  const [f, setF] = useState({ parts: "", note: "", quantity: "", supplier: "", order_ref: "", eta: "" });
  const [linked, setLinked] = useState(openIncidents.map((i) => i.incident_id));
  const [busy, setBusy] = useState(false);
  const [clientRef] = useState(() => crypto.randomUUID());
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bikes/${num}/parts`, { ...f, quantity: f.quantity || null, supplier: f.supplier || null, order_ref: f.order_ref || null, eta: f.eta || null, incident_ids: linked, client_ref: clientRef });
      toast.success("Parts order logged — bike shows Awaiting parts");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3 pt-3 border-t border-white/10 mt-3" data-testid="parts-form">
      <p className="text-xs uppercase tracking-widest text-white/40">Order / log parts — Bike {String(num).padStart(2, "0")}</p>
      <Field label="Part(s) ordered (required)"><input data-testid="parts-name" value={f.parts} onChange={set("parts")} className={inputCls} /></Field>
      <Field label="What was ordered & which repair it relates to (required)">
        <textarea data-testid="parts-note" value={f.note} onChange={set("note")} rows={2} className={inputCls + " py-3 min-h-[56px]"} />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Quantity"><input data-testid="parts-qty" value={f.quantity} onChange={set("quantity")} className={inputCls} /></Field>
        <Field label="Supplier"><input data-testid="parts-supplier" value={f.supplier} onChange={set("supplier")} className={inputCls} /></Field>
        <Field label="Order / ref no."><input data-testid="parts-ref" value={f.order_ref} onChange={set("order_ref")} className={inputCls} /></Field>
        <Field label="Expected arrival"><input data-testid="parts-eta" type="date" value={f.eta} onChange={set("eta")} className={inputCls} /></Field>
      </div>
      {openIncidents.map((i) => (
        <label key={i.incident_id} className="flex items-center gap-3 min-h-[44px] cursor-pointer text-sm">
          <input type="checkbox" className="h-5 w-5 accent-[#ff2e4d]" checked={linked.includes(i.incident_id)}
            onChange={(e) => setLinked(e.target.checked ? [...linked, i.incident_id] : linked.filter((x) => x !== i.incident_id))} />
          <span className="text-white/80">For fault {i.incident_id} — {i.description.slice(0, 50)}</span>
        </label>
      ))}
      <BigBtn data-testid="parts-save" onClick={submit} disabled={busy || !f.parts.trim() || !f.note.trim()}>Save parts order</BigBtn>
      <button onClick={onCancel} className="text-xs text-white/40 min-h-[44px] w-full">Cancel</button>
    </div>
  );
}

function StatPill({ label, value, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="rounded-xl border border-white/15 bg-black/30 px-3 py-2 text-center active:scale-95 transition min-w-[64px]">
      <span className="block text-xl font-bold" style={{ fontFamily: "Anton, sans-serif" }}>{value}</span>
      <span className="block text-[10px] uppercase tracking-wide text-white/50">{label}</span>
    </button>
  );
}

export default function BikeDetail({ num, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [panel, setPanel] = useState(null); // report | fix | parts
  const [periodView, setPeriodView] = useState(null);
  const [voiding, setVoiding] = useState(null); // {type: 'void'|'reopen', id}
  const [voidReason, setVoidReason] = useState("");
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const kind = getProfile()?.kind;
  const canFix = kind === "staff" || kind === "manager";
  const isManager = kind === "manager";

  const load = () => api.get(`/bikes/${num}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setD(null); setPanel(null); setPeriodView(null); load(); }, [num]); // eslint-disable-line
  useEffect(() => {
    if (d) document.querySelector('[data-testid="bike-detail-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [d?.bike?.bike_number]); // eslint-disable-line

  const refresh = () => { load(); onChanged(); };
  const openInc = d ? d.incidents.filter((i) => i.status === "open") : [];

  const showPeriod = async (period) => {
    try {
      const { data } = await api.get(`/bikes/${num}/incidents?period=${period}`);
      setPeriodView({ period, ...data });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doVoid = async () => {
    try {
      if (voiding.type === "reopen") {
        await api.post(`/bikes/${num}/incidents/${voiding.id}/reopen`, { reason: voidReason });
        toast.success("Reopened — no new fault counted");
      } else {
        await api.post(`/bikes/${num}/incidents/${voiding.id}/void`, { reason: voidReason });
        toast.success("Record voided — kept in the audit trail, removed from statistics");
      }
      setVoiding(null); setVoidReason(""); setPeriodView(null); refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!d) return <Panel className="mt-5 p-5"><Spinner /></Panel>;
  const S = STATUS_UI[d.bike.status] || STATUS_UI.operational;

  return (
    <Panel className="mt-5 p-5" data-testid="bike-detail-panel">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-2xl uppercase italic" style={{ fontFamily: "Anton, sans-serif" }}>Bike {String(num).padStart(2, "0")}</h3>
          <div className="flex flex-wrap gap-2 mt-1">
            <span data-testid="bike-detail-status" className={`text-xs font-bold uppercase rounded-full border px-2.5 py-1 ${S.cls} ${S.bg}`}>{S.label}</span>
            <span data-testid="bike-detail-availability" className={`text-xs font-bold uppercase rounded-full border px-2.5 py-1 ${d.bike.available ? "border-emerald-500/50 text-emerald-400" : "border-[#ff2e4d]/60 text-[#ff2e4d]"}`}>
              {d.bike.available ? "Available" : "Out of service"}
            </span>
          </div>
          {d.bike.status_note && <p className="text-sm text-white/60 mt-1.5" data-testid="bike-detail-note">{d.bike.status_note}</p>}
          {d.bike.parts_received_pending && <p className="text-xs text-amber-400 mt-1 font-semibold">Parts received — repair pending</p>}
          <p className="text-xs text-white/40 mt-1">Last service: <b className="text-purple-300" data-testid="bike-last-service">{fmtD(d.stats.last_service)}</b></p>
        </div>
        <button onClick={onClose} aria-label="Close" className="h-11 w-11 flex items-center justify-center text-white/40"><X size={20} /></button>
      </div>

      <div className="mt-4">
        <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Fault history / reliability</p>
        <div className="grid grid-cols-5 gap-2" data-testid="bike-stats">
          <StatPill label="All-time*" value={d.stats.all_time} onClick={() => showPeriod("all")} testid="stat-all" />
          <StatPill label="3 mo" value={d.stats["3m"]} onClick={() => showPeriod("3m")} testid="stat-3m" />
          <StatPill label="6 mo" value={d.stats["6m"]} onClick={() => showPeriod("6m")} testid="stat-6m" />
          <StatPill label="12 mo" value={d.stats["12m"]} onClick={() => showPeriod("12m")} testid="stat-12m" />
          <StatPill label="24 mo" value={d.stats["24m"]} onClick={() => showPeriod("24m")} testid="stat-24m" />
        </div>
        <p className="text-[11px] text-white/35 mt-1.5">*Recorded faults since records began. Rolling calendar months, Perth time — periods overlap, don't add them. 0 = no faults recorded (not a guarantee none occurred). Open: <b className="text-white/70">{d.stats.open}</b> · Last fault: {fmtD(d.stats.last_fault)}{d.stats.earliest_fault ? ` · First recorded: ${fmtD(d.stats.earliest_fault)}` : ""}</p>
      </div>

      {periodView && (
        <div className="mt-3 rounded-xl border border-white/15 bg-black/30 p-3" data-testid="period-incidents">
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs uppercase tracking-widest text-white/50">{periodView.period === "all" ? "All recorded incidents" : `Incidents — last ${periodView.period.replace("m", " months")}`} ({periodView.count})</p>
            <button onClick={() => setPeriodView(null)} className="text-white/40 h-8 w-8"><X size={16} /></button>
          </div>
          {periodView.incidents.length === 0 && <p className="text-sm text-white/40">No faults recorded in this period.</p>}
          {periodView.incidents.map((i) => (
            <div key={i.incident_id} className="text-sm py-2 border-b border-white/5 last:border-0">
              <p className="text-white/80">{i.incident_id} — {i.description}
                <span className={`ml-2 text-[10px] uppercase font-bold ${i.status === "open" ? "text-[#ff2e4d]" : "text-emerald-400"}`}>{i.status}</span>
                {i.legacy && <span className="ml-1 text-[10px] text-white/35">imported</span>}
              </p>
              <p className="text-xs text-white/40">Occurred {fmtDT(i.occurred_at || i.reported_at)} · by {i.reporter}</p>
              {isManager && i.status !== "voided" && (
                <div className="flex gap-2 mt-1">
                  <button data-testid={`void-${i.incident_id}`} onClick={() => { setVoiding({ type: "void", id: i.incident_id }); setVoidReason(""); }} className="text-[11px] text-white/40 underline min-h-[32px]">Void (duplicate / error)</button>
                  {i.status === "resolved" && (
                    <button data-testid={`reopen-${i.incident_id}`} className="text-[11px] text-amber-400 underline min-h-[32px]"
                      onClick={() => { setVoiding({ type: "reopen", id: i.incident_id }); setVoidReason(""); }}>Reopen</button>
                  )}
                </div>
              )}
            </div>
          ))}
          {voiding && (
            <div className="mt-2 space-y-2">
              <input data-testid="void-reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                placeholder={voiding.type === "reopen" ? "Required reason (why was it incorrectly closed?)" : "Required reason (e.g. duplicate of BK01-ABC123)"} className={inputCls} />
              <div className="flex gap-2">
                <GhostBtn data-testid="void-confirm" onClick={doVoid} disabled={!voidReason.trim()}
                  className={voiding.type === "reopen" ? "!border-amber-400 !text-amber-400" : "!border-[#ff2e4d] !text-[#ff2e4d]"}>
                  {voiding.type === "reopen" ? `Reopen ${voiding.id}` : `Void ${voiding.id}`}
                </GhostBtn>
                <GhostBtn onClick={() => setVoiding(null)}>Cancel</GhostBtn>
              </div>
            </div>
          )}
        </div>
      )}

      {openInc.length > 0 && !panel && (
        <div className="mt-4 space-y-2" data-testid="open-faults-list">
          {openInc.map((i) => (
            <div key={i.incident_id} className="rounded-xl border border-[#ff2e4d]/40 bg-[#ff2e4d]/5 p-3 text-sm">
              <p className="text-[#ff2e4d] font-semibold">{i.incident_id} · OPEN FAULT</p>
              <p className="text-white/80">{i.description}</p>
              <p className="text-xs text-white/40 mt-1">Reported by {i.reporter} · {fmtDT(i.reported_at)}{i.occurred_at && i.occurred_at !== i.reported_at ? ` · occurred ${fmtDT(i.occurred_at)}` : ""}</p>
              {(i.updates || []).map((u, idx) => (
                <p key={idx} className="text-xs text-white/55 mt-1 border-l-2 border-white/15 pl-2">{u.note} — {u.by}, {fmtDT(u.at)}</p>
              ))}
            </div>
          ))}
        </div>
      )}

      {!panel ? (
        <div className="mt-4 space-y-2">
          <BigBtn data-testid="report-fault-button" onClick={() => setPanel("report")}>Report a fault</BigBtn>
          {canFix && (
            <div className="grid grid-cols-2 gap-2">
              <BigBtn data-testid="fix-bike-button" className="!bg-emerald-500 !text-black flex items-center justify-center gap-2" onClick={() => setPanel("fix")}><Wrench size={18} /> Fix bike</BigBtn>
              <BigBtn data-testid="order-parts-button" className="!bg-amber-400 !text-black flex items-center justify-center gap-2" onClick={() => setPanel("parts")}><Package size={18} /> Order / log parts</BigBtn>
            </div>
          )}
        </div>
      ) : panel === "report" ? (
        <ReportFault num={num} openIncidents={openInc} onDone={() => { setPanel(null); refresh(); }} onCancel={() => setPanel(null)} />
      ) : panel === "fix" ? (
        <FixBike num={num} openIncidents={openInc} onDone={() => { setPanel(null); refresh(); }} onCancel={() => setPanel(null)} />
      ) : (
        <OrderParts num={num} openIncidents={openInc} onDone={() => { setPanel(null); refresh(); }} onCancel={() => setPanel(null)} />
      )}

      {d.parts.filter((o) => o.status !== "cancelled" && o.status !== "installed").length > 0 && (
        <div className="mt-4 space-y-2" data-testid="parts-orders">
          <p className="text-xs uppercase tracking-widest text-white/40">Parts orders</p>
          {d.parts.filter((o) => o.status !== "cancelled" && o.status !== "installed").map((o) => (
            <div key={o.order_id} className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-3 text-sm">
              <p className="text-amber-400 font-semibold">{o.order_id} · {o.status === "received" ? "PARTS RECEIVED — REPAIR PENDING" : "ORDERED"}</p>
              <p className="text-white/80">{o.parts}{o.quantity ? ` × ${o.quantity}` : ""} — {o.note}</p>
              <p className="text-xs text-white/40">{o.supplier ? `${o.supplier} · ` : ""}{o.eta ? `ETA ${o.eta} · ` : ""}by {o.by}</p>
              {canFix && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {o.status === "ordered" && <GhostBtn data-testid={`parts-received-${o.order_id}`} onClick={async () => { try { await api.post(`/bikes/${num}/parts/${o.order_id}`, { action: "received" }); toast.info("Parts received — repair still pending"); refresh(); } catch (e) { toast.error(errMsg(e)); } }}>Mark received</GhostBtn>}
                  {o.status === "received" && <GhostBtn data-testid={`parts-installed-${o.order_id}`} onClick={async () => { try { await api.post(`/bikes/${num}/parts/${o.order_id}`, { action: "installed" }); refresh(); } catch (e) { toast.error(errMsg(e)); } }}>Mark installed</GhostBtn>}
                  <GhostBtn data-testid={`parts-cancel-${o.order_id}`} onClick={() => { setCancelOrder(cancelOrder === o.order_id ? null : o.order_id); setCancelReason(""); }}>Cancel order</GhostBtn>
                </div>
              )}
              {canFix && cancelOrder === o.order_id && (
                <div className="mt-2 space-y-2">
                  <input data-testid="cancel-order-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Required reason for cancelling" className={inputCls} />
                  <div className="flex gap-2">
                    <GhostBtn data-testid="cancel-order-confirm" disabled={!cancelReason.trim()} className="!border-[#ff2e4d] !text-[#ff2e4d]"
                      onClick={async () => { try { await api.post(`/bikes/${num}/parts/${o.order_id}`, { action: "cancelled", reason: cancelReason }); setCancelOrder(null); refresh(); } catch (e) { toast.error(errMsg(e)); } }}>Confirm cancel</GhostBtn>
                    <GhostBtn onClick={() => setCancelOrder(null)}>Keep order</GhostBtn>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-5">
        <p className="text-xs uppercase tracking-widest text-white/40 mb-2">History</p>
        {d.events.length === 0 ? <p className="text-sm text-white/35">No recorded events yet.</p> : (
          <ul className="space-y-2.5 max-h-80 overflow-y-auto" data-testid="bike-timeline">
            {d.events.map((ev) => {
              const cls = EV_CLS[ev.color] || EV_CLS.neutral;
              return (
                <li key={ev.event_id} className={`text-sm border-l-2 pl-3 ${cls.split(" ")[0]}`}>
                  <span className={`uppercase text-[10px] font-bold ${cls.split(" ")[1]}`}>{EV_LABEL[ev.type] || ev.type}</span>
                  <span className="text-white/75"> — {ev.description}</span>
                  {(ev.detail?.part || ev.detail?.parts) && <span className="text-white/50"> · Part: {ev.detail.part || ev.detail.parts}{ev.detail.quantity ? ` × ${ev.detail.quantity}` : ""}</span>}
                  {(ev.detail?.photos || []).length > 0 && (
                    <span className="block mt-1 flex gap-1.5">
                      {ev.detail.photos.map((pid) => <img key={pid} src={photoUrl(pid)} alt="evidence" className="h-12 w-12 object-cover rounded-lg border border-white/15" />)}
                    </span>
                  )}
                  <p className="text-xs text-white/35">{ev.by} · {fmtDT(ev.at)}{ev.detail?.occurred_at ? ` (occurred ${fmtDT(ev.detail.occurred_at)})` : ""}{ev.detail?.performed_at ? ` (performed ${fmtDT(ev.detail.performed_at)})` : ""}</p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Panel>
  );
}
